// frontend/src/pages/admin/Activity.js
//
// The staff audit trail. Every consequential action — money leaving the Stripe
// balance, a locker being opened remotely, a dispute being resolved — has been
// written to /audit since the launch round, and nothing has ever read it back.
// An unread log is not an audit trail; it is only a promise of one.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '../../routing';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { el as elLocale } from 'date-fns/locale';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronRight, X } from 'lucide-react';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const PAGE_SIZE = 50;

// The actions the backend actually writes. Anything else still renders — with
// its raw name — rather than being dropped, because a log that hides the entries
// it was not told about is worse than useless during an incident.
const ACTIONS = [
  'deposit.refund',
  'rental.refund',
  'rental.status',
  'dispute.open',
  'dispute.resolve',
  'locker.unlock',
  'locker.screen_push',
  'product.update',
];

// Colour by consequence, not by subsystem: the two that move money and the one
// that opens a physical door are the entries someone scans a page looking for.
const ACTION_ACCENTS = {
  'deposit.refund':  theme.amber,
  'rental.refund':   theme.blue,
  'locker.unlock':   theme.redBrick,
  // Not money and not a door, but it is the only entry that changes what a
  // customer standing at the machine is told — including which number to call.
  'locker.screen_push': theme.grey600,
  'dispute.open':    theme.purple,
  'dispute.resolve': theme.green,
  'rental.status':   theme.grey600,
  'product.update':  theme.grey600,
};

// The interceptor flattens a JSON error body and adds `status`; `err.error` is
// the machine code and `err.message` the sentence worth showing. Anything
// without `error` is a raw axios failure whose own `.message` is
// "Request failed with status code 500" — never something to show an operator.
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

export default function AdminActivity() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'el' ? elLocale : undefined;

  // The entity filter lives in the URL so that /admin/activity?entity_id=<id>
  // is a working deep link from anywhere — including screens this change does
  // not own — and so the back button behaves.
  const [searchParams, setSearchParams] = useSearchParams();
  const entityId = searchParams.get('entity_id') || '';

  const [actionFilter, setActionFilter] = useState('all');
  const [actorFilter, setActorFilter]   = useState('all');
  const [page, setPage]     = useState(1);
  const [actions, setActions]   = useState([]);
  const [count, setCount]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [expanded, setExpanded] = useState({});
  // The API filters by actor_id, but only an email is worth showing in a
  // dropdown. The map is built up from every response and never pruned: pruning
  // it to the current page would empty the dropdown the moment a filter was
  // applied, taking away the only way back out.
  const [knownActors, setKnownActors] = useState({});

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => { setPage(1); }, [actionFilter, actorFilter, entityId]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
    if (actionFilter !== 'all') params.set('action', actionFilter);
    if (actorFilter !== 'all') params.set('actor_id', actorFilter);
    if (entityId) params.set('entity_id', entityId);
    api.get(`/audit?${params}`)
      .then(d => {
        const rows = Array.isArray(d?.actions) ? d.actions : [];
        setActions(rows);
        setCount(Number(d?.count) || 0);
        setKnownActors(prev => {
          const next = { ...prev };
          rows.forEach(r => { if (r.actor_id) next[r.actor_id] = r.actor_email || r.actor_id; });
          return next;
        });
      })
      // An empty table here would read as "nobody has touched anything", which
      // during an investigation is the most misleading answer available.
      .catch(err => setLoadError(serverText(err, t('admin.activity.loadFailedDetail'))))
      .finally(() => setLoading(false));
  }, [page, actionFilter, actorFilter, entityId, t]);

  useEffect(() => { load(); }, [load]);

  const clearEntity = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('entity_id');
    setSearchParams(next, { replace: true });
  };

  const actorOptions = useMemo(
    () => Object.entries(knownActors).sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
    [knownActors]
  );

  // i18next reads '.' as a key separator, so `deposit.refund` cannot be a leaf
  // name in the locale files — it would be looked up as two more levels of
  // nesting. The stored keys use underscores; the wire format keeps the dots.
  const actionLabel = (a) => t(`admin.activity.action.${String(a).replace(/\./g, '_')}`, { defaultValue: a });
  const entityLabel = (e) => t(`admin.activity.entity.${e}`, { defaultValue: e });
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, marginBottom: 4 }}>{t('admin.activity.title')}</h1>
        <p style={{ color: theme.grey400, marginBottom: 28 }}>{t('admin.activity.subtitle')}</p>

        {entityId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 6, padding: '12px 18px', marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ color: theme.goldMoss, fontSize: 13 }}>
              {t('admin.activity.filteredByEntity', { id: entityId })}
            </span>
            <button onClick={clearEntity}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: `1px solid ${theme.sandDeep}`, color: theme.goldInk, borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontFamily: theme.font.body }}>
              <X size={12} /> {t('admin.activity.clearFilter')}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <label style={labelStyle}>{t('admin.activity.actionLabel')}</label>
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} style={{ ...inputStyle, cursor: 'pointer', minWidth: 200 }}>
              <option value="all">{t('admin.activity.allActions')}</option>
              {ACTIONS.map(a => <option key={a} value={a}>{actionLabel(a)}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t('admin.activity.actorLabel')}</label>
            <select value={actorFilter} onChange={e => setActorFilter(e.target.value)} style={{ ...inputStyle, cursor: 'pointer', minWidth: 220 }}>
              <option value="all">{t('admin.activity.allActors')}</option>
              {actorOptions.map(([id, email]) => <option key={id} value={id}>{email}</option>)}
            </select>
          </div>
          <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 20px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: theme.grey600, fontFamily: theme.font.body }}>
            <RefreshCw size={13} /> {t('admin.activity.refresh')}
          </button>
        </div>

        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: theme.cream }}>
                  {['', 'colWhen', 'colActor', 'colAction', 'colEntity', 'colIp'].map((h, i) => (
                    <th key={h || i} style={{ padding: '12px 14px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 11, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                      {h ? t(`admin.activity.${h}`) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center', color: theme.gold, fontFamily: theme.font.display, fontSize: 20 }}>{t('admin.activity.loading')}</td></tr>
                ) : loadError ? (
                  <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center' }}>
                    <AlertTriangle size={24} color={theme.redBrick} />
                    <div style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: '10px 0 6px' }}>{t('admin.activity.loadFailedTitle')}</div>
                    <p style={{ color: theme.grey400, fontSize: 13, margin: '0 0 18px' }}>{loadError}</p>
                    <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.ink, color: theme.white, border: 'none', padding: '11px 24px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                      <RefreshCw size={13} /> {t('admin.activity.retry')}
                    </button>
                  </td></tr>
                ) : actions.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.activity.empty')}</td></tr>
                ) : actions.map(a => {
                  const open = !!expanded[a.id];
                  const accent = ACTION_ACCENTS[a.action] || theme.grey600;
                  // `details` carries a different shape per action — a refund's
                  // amount, an unlock's box, a status change's before and after.
                  // Rendered whole and expandably rather than flattened into a
                  // column per field, which would be a column per action forever
                  // and would silently drop whatever the backend adds next.
                  const hasDetails = a.details && Object.keys(a.details).length > 0;
                  return (
                    <React.Fragment key={a.id}>
                      <tr style={{ borderBottom: open ? 'none' : `1px solid ${theme.creamDeep}`, cursor: hasDetails ? 'pointer' : 'default' }}
                        onClick={() => hasDetails && setExpanded(prev => ({ ...prev, [a.id]: !prev[a.id] }))}>
                        <td style={{ padding: '12px 14px', color: theme.gold, width: 24 }}>
                          {hasDetails ? (open ? <ChevronDown size={15} /> : <ChevronRight size={15} />) : null}
                        </td>
                        <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>
                          {a.created_at ? format(new Date(a.created_at), 'd MMM yyyy, HH:mm', { locale: dateLocale }) : '—'}
                        </td>
                        <td style={{ padding: '12px 14px', color: theme.ink }}>{a.actor_email || a.actor_id || '—'}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ background: `${accent}15`, color: accent, padding: '3px 12px', borderRadius: 100, fontSize: 11, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                            {actionLabel(a.action)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>
                          {a.entity_type ? entityLabel(a.entity_type) : '—'}
                          {a.entity_id && (
                            <span style={{ color: theme.grey400, fontFamily: theme.font.mono, fontSize: 11, marginLeft: 6 }}>#{String(a.entity_id).slice(0, 8)}</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', color: theme.grey400, fontFamily: theme.font.mono, fontSize: 11 }}>{a.ip || '—'}</td>
                      </tr>
                      {open && (
                        <tr style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                          <td />
                          <td colSpan={5} style={{ padding: '0 14px 14px' }}>
                            <pre style={{ margin: 0, padding: '12px 14px', background: theme.cream, border: `1px solid ${theme.creamBone}`, borderRadius: 6, fontSize: 11, lineHeight: 1.6, color: theme.ink, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflowY: 'auto' }}>
                              {JSON.stringify(a.details, null, 2)}
                            </pre>
                            {a.entity_id && (
                              <div style={{ color: theme.grey400, fontSize: 11, marginTop: 8, fontFamily: theme.font.mono }}>{a.entity_id}</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !loadError && count > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.activity.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, count),
                total: count,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.activity.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= count}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= count ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= count ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.activity.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, color: theme.grey500, letterSpacing: '0.08em', marginBottom: 6 };
const inputStyle = { padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white };
